(() => {
  if (window.hobbytronNativeSearchReady) return;
  window.hobbytronNativeSearchReady = true;
  const config = () => JSON.parse(document.querySelector('[data-ht-search-config]')?.textContent || '{}');
  const suffix = () => config().exclusions || '';
  const legacySuffix = ' -title:Mojis -title:"Mrs Bench"';
  const scoped = value => `${value}${suffix() ? ` ${suffix()}` : ''}`;
  const quote = term => JSON.stringify(term.trim());
  const normalQuery = term => scoped(term.trim() ? quote(term) : '*');
  const skuQuery = term => scoped(`variants.sku:${quote(term)}`);
  const fallbackQuery = term => scoped(`(variants.sku:${quote(term)} OR ${quote(term)})`);
  const termFrom = query => {
    let value = query.trim();
    if (suffix() && value.endsWith(suffix())) value = value.slice(0, -suffix().length).trim();
    if (value.endsWith(legacySuffix)) value = value.slice(0, -legacySuffix.length).trim();
    if (value.startsWith('(variants.sku:')) value = value.slice(1).split(' OR ')[0];
    if (value.startsWith('variants.sku:')) value = value.slice('variants.sku:'.length);
    if (value === '*') return '';
    try {
      const term = JSON.parse(value);
      return typeof term === 'string' ? term : value;
    } catch { return value; }
  };
  const isResolved = query => [normalQuery, skuQuery, fallbackQuery].some(build => build(termFrom(query)) === query);
  const lookup = async (term, context, signal) => {
    if (!term.trim()) return false;
    const url = new URL(config().url || '/search', location.origin);
    // Only query-specific Shopify results are inspected; no whole-catalog download.
    url.searchParams.set('view', 'hobbytron-sku-probe');
    url.searchParams.set('q', skuQuery(term));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    url.searchParams.set('options[unavailable_products]', 'show');
    const preview = context.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    for (const [key, value] of context.searchParams) if (key.startsWith('filter.')) url.searchParams.append(key, value);
    const timeout = new AbortController();
    const cancel = () => timeout.abort();
    signal.addEventListener('abort', cancel, { once: true });
    const timer = setTimeout(cancel, 3500);
    try {
      const response = await fetch(url.href, { credentials: 'same-origin', signal: timeout.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Native SKU lookup unavailable');
      const result = await response.json();
      if (!result.complete || !Array.isArray(result.matches)) throw new Error('Incomplete native SKU lookup');
      return result.matches.some(item => String(item.sku).trim().toLocaleLowerCase() === term.trim().toLocaleLowerCase());
    } finally { clearTimeout(timer); signal.removeEventListener('abort', cancel); }
  };
  const resolveQuery = async (term, context, signal) => {
    try {
      if (await lookup(term, context, signal)) return skuQuery(term);
      // Also accept a code pasted alongside a product name. Candidates only
      // become SKU searches after Shopify confirms an actual variant SKU.
      const tokens = term.trim().split(/\s+/);
      if (tokens.length > 1) {
        const candidates = [...new Set(tokens.filter(token => /\d/.test(token)))].slice(0, 4);
        const matches = await Promise.all(candidates.map(async candidate => await lookup(candidate, context, signal) ? candidate : null));
        const match = matches.find(Boolean);
        if (match) return skuQuery(match);
      }
      return normalQuery(term);
    }
    catch (error) {
      if (signal.aborted) throw error;
      // A failed probe must not restore the old SKU-dropping phrase-only query.
      return term.trim() ? fallbackQuery(term) : normalQuery(term);
    }
  };
  const parameters = url => {
    url.searchParams.set('view', 'hobbytron-shell');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    url.searchParams.set('options[unavailable_products]', 'show');
    return url;
  };
  const init = root => {
    root.querySelectorAll('[data-ht-shell-search]:not([data-ht-search-ready])').forEach(form => {
      const input = form.querySelector('[data-ht-search-input]');
      const query = form.querySelector('[name="q"]');
      if (!input || !query) return;
      form.dataset.htSearchReady = 'true';
      input.value = termFrom(input.value);
      let controller;
      form.addEventListener('reset', () => { controller?.abort(); form.removeAttribute('aria-busy'); });
      form.addEventListener('submit', async event => {
        event.preventDefault();
        controller?.abort();
        controller = new AbortController();
        const active = controller;
        const term = input.value.trim();
        input.value = term;
        form.setAttribute('aria-busy', 'true');
        // A new query resets filters/pages as the existing GET form did.
        const target = parameters(new URL(form.action, location.href));
        const current = new URL(location.href);
        const preview = new FormData(form).get('preview_theme_id') || current.searchParams.get('preview_theme_id');
        if (preview) target.searchParams.set('preview_theme_id', preview);
        try {
          const resolved = await resolveQuery(term, target, active.signal);
          if (active.signal.aborted) return;
          query.value = resolved;
          target.searchParams.set('q', resolved);
          location.assign(target.href);
        } catch (error) { if (!active.signal.aborted) throw error; }
        finally { if (controller === active) form.removeAttribute('aria-busy'); }
      });
    });
    const guard = root.querySelector('[data-ht-search-scope-guard]');
    const results = root.querySelector('[data-ht-search-query]');
    const currentQuery = guard ? guard.dataset.query : results?.dataset.htSearchQuery;
    if (currentQuery === undefined || isResolved(currentQuery)) return;
    const current = new URL(location.href);
    const isShell = !!guard || results?.dataset.htSearchScoped === 'true';
    const controller = new AbortController();
    resolveQuery(termFrom(currentQuery), current, controller.signal).then(resolved => {
      // Legacy title search retains its native presentation; exact SKU bookmarks
      // enter the same product-search experience as the shared header.
      if (!isShell && !resolved.startsWith('variants.sku:')) return;
      const target = parameters(current);
      target.searchParams.set('q', resolved);
      target.searchParams.delete('page');
      location.replace(target.href);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
